import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { NEOGTM_LOGO_PNG_URL } from '@/lib/brandAssets';
import {
  appendAnalyticsWindow,
  coerceAnalyticsWindow,
  formatAnalyticsWindowLabel,
  previousInclusiveRange,
  windowDayCount,
} from '@/lib/analyticsWindow';

// ─── constants ─────────────────────────────────────────────────────────────
const PAGE_W = 842;
const PAGE_H = 595;
const M = 24;
const INNER_W = PAGE_W - 2 * M;

const COLORS = {
  brand: [10, 26, 47],
  tileBg: [247, 248, 250],
  border: [229, 231, 235],
  text: [10, 26, 47],
  muted: [107, 114, 128],
  accent: [0, 194, 194],
  green: [16, 185, 129],
  greenSoft: [223, 247, 235],
  amber: [245, 158, 11],
  red: [220, 38, 38],
  white: [255, 255, 255],
  cloudBg: [248, 250, 252],
  cloudBorder: [241, 245, 249],
};

const PIE_PALETTE = [
  [0, 194, 194],
  [10, 26, 47],
  [245, 158, 11],
  [16, 185, 129],
  [99, 102, 241],
  [236, 72, 153],
  [234, 179, 8],
  [107, 114, 128],
];

const KNOWN_TENANT_LABELS = {
  instructure: 'Instructure',
  canvas: 'Canvas',
  neogtm: 'NeoGTM',
  evasys: 'evasys',
  evasys_v2: 'evasys',
  evaexam: 'evaexam',
  synap: 'Synap',
  learnwise: 'LearnWise',
  edusign: 'Edusign',
  liaison: 'Liaison',
  vretta: 'Vretta',
  surpass: 'Surpass',
  surpass_v2: 'Surpass',
  fullfabric: 'Full Fabric',
  fullfabric_v2: 'Full Fabric',
  edqwest: 'EdQwest',
  seba: 'Seba',
  rumi: 'Rumi',
  studentcrm: 'StudentCRM',
  unit4: 'Unit4',
  blackboard: 'Blackboard',
  constructor_tech: 'Constructor',
  testwe: 'TestWe',
  efekta: 'Efekta',
  jpik: 'JPIK',
  positivo: 'Positivo',
  feedbackfruits: 'FeedbackFruits',
  feedbackfruits_v2: 'FeedbackFruits',
  demarque: 'Demarque',
  demarque_v2: 'Demarque',
  bibliotheca: 'Bibliotheca',
};

const fmtInt = (n) => (Number.isFinite(+n) ? Number(n).toLocaleString('en-US') : '—');
const fmtPct = (n, d = 1) => (Number.isFinite(+n) ? `${Number(n).toFixed(d)}%` : '—');
const fmtNum = (n, d = 1) => (Number.isFinite(+n) ? Number(n).toFixed(d) : '—');
const truncate = (s, n) => {
  const str = String(s ?? '');
  return str.length <= n ? str : str.slice(0, Math.max(0, n - 1)) + '…';
};

function resolveApiBaseUrl(apiBaseUrl) {
  if (apiBaseUrl) return apiBaseUrl.replace(/\/$/, '');
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';
}

export function formatTenantLabelForPdf(tenantId) {
  const id = String(tenantId || '').trim();
  if (!id) return 'Tenant';
  const mapped = KNOWN_TENANT_LABELS[id.toLowerCase()];
  if (mapped) return mapped;
  return id
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ')
    .trim();
}

function normalizeRow(data) {
  if (data == null) return {};
  if (Array.isArray(data)) return data[0] && typeof data[0] === 'object' ? data[0] : {};
  return typeof data === 'object' ? data : {};
}

function pickNum(obj, keys) {
  for (const k of keys) {
    const v = Number(obj?.[k]);
    if (Number.isFinite(v)) return v;
  }
  return NaN;
}

/** Prefer the same count field on both rows (overview vs summary shapes differ). */
function pickAlignedCount(wideRow, narrowRow, keys) {
  for (const k of keys) {
    const wide = Number(wideRow?.[k]);
    const narrow = Number(narrowRow?.[k]);
    if (Number.isFinite(wide) && Number.isFinite(narrow)) {
      return Math.max(0, wide - narrow);
    }
  }
  const wide = pickNum(wideRow, keys);
  const narrow = pickNum(narrowRow, keys);
  if (!Number.isFinite(wide) || !Number.isFinite(narrow)) return NaN;
  return Math.max(0, wide - narrow);
}

const SENT_COUNT_KEYS = ['total_sent', 'total_emails_sent'];
const OPEN_COUNT_KEYS = ['total_opened', 'total_opens', 'total_human_opens'];
const CLICK_COUNT_KEYS = [
  'total_clicked',
  'total_human_clicks',
  'human_clicks',
  'total_clicks',
  'unique_messages_clicked',
];
const REPLY_COUNT_KEYS = ['total_replied', 'total_replies'];

function impliedRate(num, den) {
  const n = Number(num);
  const d = Number(den);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) return null;
  return (n / d) * 100;
}

// ─── logo loader ──────────────────────────────────────────────────────────
const LOGO_CANDIDATE_URLS = [
  NEOGTM_LOGO_PNG_URL,
  '/logo.png',
  '/neogtm-logo.png',
  '/logos/neogtm.png',
  '/assets/logo.png',
  '/static/logo.png',
  '/images/logo.png',
  '/logo.svg',
];

async function loadLogoDataUrl(explicit) {
  if (explicit) return explicit;
  if (typeof window === 'undefined') return null;
  const origin = window.location.origin;
  for (const p of LOGO_CANDIDATE_URLS) {
    try {
      const url = /^https?:\/\//i.test(p) ? p : origin + p;
      const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
      if (!res.ok) continue;
      const blob = await res.blob();
      if (!blob || blob.size === 0) continue;
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      });
      return dataUrl;
    } catch {
      // try next
    }
  }
  return null;
}

// ─── primitives ─────────────────────────────────────────────────────────────
function fillRect(pdf, x, y, w, h, color, radius = 0) {
  pdf.setFillColor(...color);
  if (radius > 0) pdf.roundedRect(x, y, w, h, radius, radius, 'F');
  else pdf.rect(x, y, w, h, 'F');
}

function strokeRect(pdf, x, y, w, h, color, weight = 0.5, radius = 0) {
  pdf.setDrawColor(...color);
  pdf.setLineWidth(weight);
  if (radius > 0) pdf.roundedRect(x, y, w, h, radius, radius, 'S');
  else pdf.rect(x, y, w, h, 'S');
}

function text(pdf, str, x, y, opts = {}) {
  const { size = 9, color = COLORS.text, weight = 'normal', align = 'left' } = opts;
  pdf.setFontSize(size);
  pdf.setTextColor(...color);
  pdf.setFont('helvetica', weight);
  pdf.text(String(str ?? ''), x, y, { align });
}

// Count/volume KPIs: relative change vs prior (same rule as before v12).
function fmtDelta(curr, prev) {
  if (!Number.isFinite(+curr) || !Number.isFinite(+prev) || prev === 0) {
    return { color: COLORS.muted, text: '—' };
  }
  const diff = Number(curr) - Number(prev);
  const relPct = (diff / Math.abs(prev)) * 100;
  if (Math.abs(relPct) < 1) return { color: COLORS.muted, text: '—' };
  return {
    color: diff > 0 ? COLORS.green : COLORS.amber,
    text: `${diff > 0 ? '+' : ''}${relPct.toFixed(0)}%`,
  };
}

// Open / click / reply tiles: percentage-point delta (matches Analytics overview cards).
function fmtRateDelta(curr, prev) {
  if (!Number.isFinite(+curr) || !Number.isFinite(+prev)) {
    return { color: COLORS.muted, text: '—' };
  }
  const diff = Number(curr) - Number(prev);
  if (Math.abs(diff) < 0.05) return { color: COLORS.muted, text: '—' };
  return {
    color: diff > 0 ? COLORS.green : COLORS.amber,
    text: `${diff > 0 ? '+' : ''}${diff.toFixed(1)} pp`,
  };
}

// [v9] `generatedOn` (a Date) renders as "Generated on: <YYYY-MM-DD>" in the
// top-right of the header. Only page 1 passes it; subsequent pages omit.
function drawHeaderBand(pdf, { tenantLabel, description, pageStr, generatedOn }) {
  const y = M;
  const h = 44;
  fillRect(pdf, M, y, INNER_W, h, COLORS.brand, 4);
  text(pdf, tenantLabel, M + 14, y + 19, { size: 16, color: COLORS.white, weight: 'bold' });
  text(pdf, description, M + 14, y + 35, { size: 9, color: [200, 220, 235] });
  if (generatedOn) {
    const d = generatedOn instanceof Date ? generatedOn : new Date();
    const stamp = d.toISOString().slice(0, 10);
    text(pdf, `Generated on: ${stamp}`, M + INNER_W - 14, y + 19, {
      size: 9, color: COLORS.white, align: 'right',
    });
    text(pdf, pageStr, M + INNER_W - 14, y + 35, {
      size: 9, color: [200, 220, 235], align: 'right',
    });
  } else {
    // Same baseline as page 1's “Page 1 of N” (below optional generated line slot).
    text(pdf, pageStr, M + INNER_W - 14, y + 35, {
      size: 9,
      color: [200, 220, 235],
      align: 'right',
    });
  }
}

// [v9] Footer is a slim navy band (same brand color as the header) spanning
// the full inner width, with the logo image overlaying its right edge.
const FOOTER_STRIP_H = 20;
const FOOTER_STRIP_PAD_BOTTOM = 4;

function drawBrandFooter(pdf, logoDataUrl) {
  const stripY = PAGE_H - FOOTER_STRIP_H - FOOTER_STRIP_PAD_BOTTOM;
  fillRect(pdf, M, stripY, INNER_W, FOOTER_STRIP_H, COLORS.brand, 4);

  if (logoDataUrl) {
    try {
      const logoH = 16;
      const logoW = 56;
      const padRight = 8;
      const lx = M + INNER_W - logoW - padRight;
      const ly = stripY + (FOOTER_STRIP_H - logoH) / 2;
      pdf.addImage(logoDataUrl, undefined, lx, ly, logoW, logoH, undefined, 'FAST');
      return;
    } catch (err) {
      console.warn('[analyticsPdfExport] logo embed failed; falling back to wordmark', err);
    }
  }
  text(pdf, 'neogtm', M + INNER_W - 10, stripY + 14, {
    size: 10,
    color: COLORS.white,
    weight: 'bold',
    align: 'right',
  });
}

function drawTile(pdf, x, y, w, h, { label, value, delta }) {
  fillRect(pdf, x, y, w, h, COLORS.tileBg, 3);
  strokeRect(pdf, x, y, w, h, COLORS.border, 0.5, 3);
  text(pdf, String(label).toUpperCase(), x + 10, y + 14, { size: 7.5, color: COLORS.muted, weight: 'bold' });
  text(pdf, value, x + 10, y + 38, { size: 22, color: COLORS.text, weight: 'bold' });
  if (delta && delta.text) {
    text(pdf, delta.text, x + 10, y + h - 10, { size: 8, color: delta.color });
  }
}

// [2026-07-08] The analytics endpoints each paginate large tables. Fired all
// at once they overwhelm Postgres — latencies climb past 20s and a random
// endpoint returns 500 (statement timeout), leaving a blank section in the
// PDF. Two mitigations work together:
//   1. runPool() below caps how many run at once (bounded DB contention).
//   2. fetchJson retries transient failures (5xx / network) so a single
//      timeout during the burst recovers instead of blanking a page.
// A non-2xx or network error throws (so the pool retries); a 2xx with an empty
// body is a legitimate "no data" and is returned as-is.
async function fetchJson(url, { retries = 2, retryDelayMs = 1000 } = {}) {
  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const res = await fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } });
      if (res.ok) return await res.json();
      // 4xx won't fix on retry; bail immediately. 5xx = transient, retry.
      if (res.status < 500) return null;
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      lastErr = e;
    }
    if (attempt < retries) {
      await new Promise((r) => setTimeout(r, retryDelayMs * (attempt + 1)));
    }
  }
  console.warn('[analyticsPdfExport] fetch failed after retries:', url, lastErr?.message);
  return null;
}

// Run async task factories with bounded concurrency so the analytics endpoints
// don't all hammer Postgres simultaneously. Preserves input order in the result.
async function runPool(taskFns, concurrency = 3) {
  const results = new Array(taskFns.length);
  let next = 0;
  const worker = async () => {
    while (next < taskFns.length) {
      const i = next;
      next += 1;
      try {
        results[i] = await taskFns[i]();
      } catch (e) {
        console.warn('[analyticsPdfExport] pool task failed:', e?.message);
        results[i] = null;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));
  return results;
}

/** Same source as Analytics overview cards: overview + from/to, then summary fallback. */
async function fetchEmailOverviewRow(base, tenantId, windowOrDays, userId) {
  const window = coerceAnalyticsWindow(windowOrDays);
  const qo = new URLSearchParams({ tenant_id: tenantId });
  if (window.mode === 'custom') {
    appendAnalyticsWindow(qo, window);
  } else {
    const rangeDays = windowDayCount(window);
    const to = new Date().toISOString();
    const from = new Date(Date.now() - rangeDays * 24 * 60 * 60 * 1000).toISOString();
    qo.set('from', from);
    qo.set('to', to);
    qo.set('days', window.days);
  }
  if (userId) qo.append('user_id', userId);
  let data = await fetchJson(`${base}/api/email-analytics/overview?${qo.toString()}`);
  let row = normalizeRow(data);
  if (Object.keys(row).length) return row;
  const qs = new URLSearchParams({ tenant_id: tenantId });
  appendAnalyticsWindow(qs, window);
  if (userId) qs.append('user_id', userId);
  data = await fetchJson(`${base}/api/email-analytics/summary?${qs.toString()}`);
  return normalizeRow(data);
}

function enrichSummaryRow(row) {
  const sent = pickNum(row, SENT_COUNT_KEYS);
  const opens = pickNum(row, OPEN_COUNT_KEYS);
  const clicks = pickNum(row, CLICK_COUNT_KEYS);
  const replies = pickNum(row, REPLY_COUNT_KEYS);
  const out = { ...row };
  // BE /overview returns rates as 0–100 already; pass through untouched.
  // Only compute when the BE omitted the rate field.
  if (!Number.isFinite(Number(out.open_rate))) {
    const r = impliedRate(opens, sent);
    if (r != null) out.open_rate = r;
  }
  if (!Number.isFinite(Number(out.click_rate))) {
    const r = impliedRate(clicks, sent);
    if (r != null) out.click_rate = r;
  }
  if (!Number.isFinite(Number(out.reply_rate))) {
    const r = impliedRate(replies, sent);
    if (r != null) out.reply_rate = r;
  }
  return out;
}

// [v13 2026-06-01] Prior-only rates derived from count differentials, not the
// 28-day combined rate. The pre-fix returned `priorFull.click_rate` (the
// rate over the WHOLE 28-day window, which already includes the current
// 14-day period). When click activity was steady across both halves, the
// combined rate landed within 1% of the current — the relative-threshold
// gate then suppressed the CLICK% delta as "no change", even though there
// WAS a real prior-vs-current movement once you isolated the past 14 days.
// True prior-only rate = (28d count − 14d count) / (28d sends − 14d sends).
// Falls back to `priorFull.*_rate` if the BE didn't return the raw counts
// (e.g. older summary endpoint shapes).
function buildPriorOnly(summary, priorFull) {
  if (!summary || !priorFull) return null;
  const priorSent = pickAlignedCount(priorFull, summary, SENT_COUNT_KEYS);
  const priorOpens = pickAlignedCount(priorFull, summary, OPEN_COUNT_KEYS);
  const priorClicks = pickAlignedCount(priorFull, summary, CLICK_COUNT_KEYS);
  const priorReplies = pickAlignedCount(priorFull, summary, REPLY_COUNT_KEYS);
  const priorOnlyRate = (count) => {
    if (!Number.isFinite(priorSent) || priorSent <= 0) return NaN;
    if (!Number.isFinite(count) || count < 0) return NaN;
    return (count / priorSent) * 100;
  };
  const computedOpen = priorOnlyRate(priorOpens);
  const computedClick = priorOnlyRate(priorClicks);
  const computedReply = priorOnlyRate(priorReplies);
  const activeWide = pickNum(priorFull, ['active_accounts', 'accounts_reached', 'distinct_accounts', 'unique_accounts']);
  const activeNarrow = pickNum(summary, ['active_accounts', 'accounts_reached', 'distinct_accounts', 'unique_accounts']);
  const priorAccounts =
    Number.isFinite(activeWide) && Number.isFinite(activeNarrow)
      ? Math.max(0, activeWide - activeNarrow)
      : NaN;
  return {
    total_sent: priorSent,
    open_rate: computedOpen,
    click_rate: computedClick,
    reply_rate: computedReply,
    active_accounts: priorAccounts,
    avg_touches_per_persona: pickNum(priorFull, ['avg_touches_per_persona', 'avg_touches', 'touches_per_persona']),
  };
}

/** When Analytics page already computed prior-window rates, use those for KPI deltas. */
function applyPriorPeriodRateOverrides(priorOnly, overrides) {
  if (!overrides || typeof overrides !== 'object') return priorOnly;
  const out = { ...(priorOnly || {}) };
  const map = [
    ['open_rate', overrides.open_rate ?? overrides.prev_email_open_rate_percent],
    ['click_rate', overrides.click_rate ?? overrides.prev_email_click_rate_percent],
    ['reply_rate', overrides.reply_rate ?? overrides.prev_reply_rate_percent],
  ];
  for (const [key, raw] of map) {
    if (raw === null || raw === undefined || raw === '') continue;
    const v = Number(raw);
    if (Number.isFinite(v)) out[key] = v;
  }
  return out;
}

function computeAvgTouches(summary) {
  const direct = pickNum(summary, [
    'avg_touches_per_persona',
    'avg_touches',
    'touches_per_persona',
  ]);
  if (Number.isFinite(direct)) return direct;
  return NaN;
}

// ─── trend chart — bars (open) + lines (click/reply) on dual axis ──────────
function drawTrendChart(pdf, x, y, w, h, daily) {
  fillRect(pdf, x, y, w, h, COLORS.white);
  strokeRect(pdf, x, y, w, h, COLORS.border, 0.5);
  text(pdf, 'TRUE ENGAGEMENT RATE TREND', x + 8, y + 12, { size: 8, color: COLORS.muted, weight: 'bold' });

  const legY = y + 26;
  let lx = x + 8;
  pdf.setFillColor(...COLORS.accent);
  pdf.rect(lx, legY - 5, 7, 6, 'F');
  text(pdf, 'Open (bars)', lx + 10, legY, { size: 7, color: COLORS.text });
  lx += 80;
  pdf.setDrawColor(...COLORS.brand);
  pdf.setLineWidth(1.4);
  pdf.line(lx, legY - 2, lx + 9, legY - 2);
  text(pdf, 'Click (line)', lx + 12, legY, { size: 7, color: COLORS.text });
  lx += 80;
  pdf.setDrawColor(...COLORS.amber);
  pdf.setLineWidth(1.4);
  pdf.setLineDashPattern([2, 2], 0);
  pdf.line(lx, legY - 2, lx + 9, legY - 2);
  pdf.setLineDashPattern([], 0);
  text(pdf, 'Reply (line)', lx + 12, legY, { size: 7, color: COLORS.text });

  const raw = Array.isArray(daily?.days) ? daily.days : [];
  const days = raw
    .filter((d) => d && d.date)
    .map((d) => ({
      ...d,
      sends: Number(d.sends) || 0,
      open_rate: Number(d.open_rate),
      click_rate: Number(d.click_rate),
      reply_rate: Number(d.reply_rate),
    }))
    .sort((a, b) => Date.parse(a.date) - Date.parse(b.date));

  if (days.length === 0 || days.every((d) => d.sends === 0)) {
    text(pdf, 'No sends in this window', x + w / 2, y + h / 2 + 4, { size: 10, color: COLORS.muted, align: 'center' });
    return;
  }

  const padL = 42;
  const padR = 50;
  const padT = 50;
  const padB = 22;
  const plotX = x + padL;
  const plotY = y + padT;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;

  const maxOpen = Math.max(
    0,
    ...days.map((d) => (d.sends > 0 && Number.isFinite(d.open_rate) ? d.open_rate : 0)),
  );
  const leftYMax = Math.min(100, Math.max(10, Math.ceil((maxOpen * 1.2) / 10) * 10));
  const rightYMax = 20;

  pdf.setDrawColor(...COLORS.border);
  pdf.setLineWidth(0.25);
  const leftTicks = 5;
  for (let t = 0; t <= leftTicks; t++) {
    const v = (leftYMax / leftTicks) * t;
    const py = plotY + plotH - (v / leftYMax) * plotH;
    pdf.line(plotX, py, plotX + plotW, py);
    text(pdf, `${v.toFixed(0)}%`, plotX - 4, py + 3, { size: 7, color: COLORS.muted, align: 'right' });
  }
  const rightTicks = 4;
  for (let t = 0; t <= rightTicks; t++) {
    const v = (rightYMax / rightTicks) * t;
    const py = plotY + plotH - (v / rightYMax) * plotH;
    text(pdf, `${v.toFixed(0)}%`, plotX + plotW + 4, py + 3, { size: 7, color: COLORS.muted, align: 'left' });
  }

  text(pdf, 'OPEN %', plotX, plotY - 6, { size: 7, color: COLORS.muted, weight: 'bold' });
  text(pdf, 'CLICK / REPLY %', plotX + plotW - 4, plotY - 6, {
    size: 7,
    color: COLORS.muted,
    weight: 'bold',
    align: 'right',
  });

  const n = days.length;
  const slotW = plotW / n;
  const barW = Math.max(2, Math.min(slotW * 0.7, 12));
  const xCenter = (i) => plotX + slotW * i + slotW / 2;
  const labelEvery = n > 30 ? 7 : 5;
  days.forEach((d, i) => {
    if (i === 0 || i === n - 1 || i % labelEvery === 0) {
      const dt = new Date(d.date);
      const lbl = `${dt.toLocaleString('en', { month: 'short' })} ${dt.getDate()}`;
      text(pdf, lbl, xCenter(i), plotY + plotH + 12, { size: 7, color: COLORS.muted, align: 'center' });
    }
  });

  days.forEach((d, i) => {
    if ((d.sends ?? 0) === 0) return;
    if (!Number.isFinite(d.open_rate)) return;
    const v = Math.min(Math.max(0, d.open_rate), leftYMax);
    const barH = (v / leftYMax) * plotH;
    const bx = xCenter(i) - barW / 2;
    const by = plotY + plotH - barH;
    fillRect(pdf, bx, by, barW, barH, COLORS.accent);
  });

  const drawLine = (color, weight, dashed, accessor) => {
    pdf.setDrawColor(...color);
    pdf.setLineWidth(weight);
    if (dashed) pdf.setLineDashPattern([2, 2], 0);
    else pdf.setLineDashPattern([], 0);
    let prev = null;
    days.forEach((d, i) => {
      if ((d.sends ?? 0) === 0) {
        prev = null;
        return;
      }
      const r = +accessor(d);
      if (!Number.isFinite(r)) {
        prev = null;
        return;
      }
      const v = Math.min(Math.max(0, r), rightYMax);
      const py = plotY + plotH - (v / rightYMax) * plotH;
      const px = xCenter(i);
      if (prev) pdf.line(prev.x, prev.y, px, py);
      prev = { x: px, y: py };
    });
    pdf.setLineDashPattern([], 0);
  };
  drawLine(COLORS.brand, 1.4, false, (d) => d.click_rate);
  drawLine(COLORS.amber, 1.4, true, (d) => d.reply_rate);
}

// ─── pie chart helpers ─────────────────────────────────────────────────────
function drawPie(pdf, cx, cy, r, slices) {
  const total = slices.reduce((s, x) => s + (Number(x.value) || 0), 0);
  if (total <= 0) {
    text(pdf, 'No data', cx, cy + 3, { size: 8, color: COLORS.muted, align: 'center' });
    return;
  }
  let startAngle = -Math.PI / 2;
  slices.forEach((s, idx) => {
    const value = Number(s.value) || 0;
    if (value <= 0) return;
    const sweep = (value / total) * Math.PI * 2;
    const segs = Math.max(8, Math.ceil(36 * (sweep / (Math.PI * 2))));
    const startPt = { x: cx, y: cy };
    const arcPts = [];
    for (let i = 0; i <= segs; i++) {
      const t = startAngle + (sweep * i) / segs;
      arcPts.push({ x: cx + Math.cos(t) * r, y: cy + Math.sin(t) * r });
    }
    const deltas = [];
    let cur = startPt;
    for (const p of arcPts) {
      deltas.push([p.x - cur.x, p.y - cur.y]);
      cur = p;
    }
    deltas.push([startPt.x - cur.x, startPt.y - cur.y]);
    const color = s.color || PIE_PALETTE[idx % PIE_PALETTE.length];
    pdf.setFillColor(...color);
    pdf.setDrawColor(...COLORS.white);
    pdf.setLineWidth(0.4);
    pdf.lines(deltas, startPt.x, startPt.y, [1, 1], 'FD', true);
    startAngle += sweep;
  });
}

function drawPieWithLegend(pdf, x, y, w, h, title, rows) {
  fillRect(pdf, x, y, w, h, COLORS.white);
  strokeRect(pdf, x, y, w, h, COLORS.border, 0.5);
  text(pdf, title.toUpperCase(), x + 8, y + 12, { size: 7.5, color: COLORS.muted, weight: 'bold' });

  const cleanRows = (rows || [])
    .map((r, i) => ({
      label: String(r.label ?? r.name ?? r.country ?? r.sector ?? '—').trim() || '—',
      value: Number(r.value ?? r.count ?? r.sends ?? r.total_sent ?? r.persona_count ?? r.account_count) || 0,
      color: PIE_PALETTE[i % PIE_PALETTE.length],
    }))
    .filter((r) => r.value > 0);

  if (cleanRows.length === 0) {
    text(pdf, 'No data', x + w / 2, y + h / 2 + 4, { size: 9, color: COLORS.muted, align: 'center' });
    return;
  }

  const top = cleanRows.slice(0, 5);
  if (cleanRows.length > 5) {
    const otherVal = cleanRows.slice(5).reduce((s, r) => s + r.value, 0);
    if (otherVal > 0) {
      top.push({ label: 'Other', value: otherVal, color: PIE_PALETTE[7] });
    }
  }
  const total = top.reduce((s, r) => s + r.value, 0);

  const pieAreaW = Math.min(h - 22, 80);
  const r = (pieAreaW - 14) / 2;
  const cx = x + 8 + r + 4;
  const cy = y + (h + 22) / 2;
  drawPie(pdf, cx, cy, r, top);

  const legX = x + 16 + pieAreaW;
  const legW = w - 16 - pieAreaW - 8;
  const legStartY = y + 24;
  const lineH = Math.min(11, (h - 28) / Math.max(top.length, 1));
  top.forEach((s, i) => {
    const ly = legStartY + i * lineH;
    pdf.setFillColor(...s.color);
    pdf.rect(legX, ly - 4, 6, 6, 'F');
    const pct = total > 0 ? (s.value / total) * 100 : 0;
    text(pdf, truncate(s.label, 16), legX + 10, ly + 1, { size: 7.5, color: COLORS.text });
    text(pdf, `${pct.toFixed(0)}%`, legX + legW - 4, ly + 1, { size: 7.5, color: COLORS.text, align: 'right', weight: 'bold' });
  });
}

function makeBestHighlighter(bodyRows, cols) {
  const winners = new Map();
  cols.forEach(({ col, mode = 'max' }) => {
    let bestVal = mode === 'min' ? Infinity : -Infinity;
    let bestRows = [];
    bodyRows.forEach((r, ri) => {
      const cell = r?.[col];
      const raw = typeof cell === 'string' ? cell : String(cell ?? '');
      const num = Number(raw.replace(/[,%\s]/g, ''));
      if (!Number.isFinite(num)) return;
      if ((mode === 'min' && num < bestVal) || (mode !== 'min' && num > bestVal)) {
        bestVal = num;
        bestRows = [ri];
      } else if (num === bestVal) {
        bestRows.push(ri);
      }
    });
    if (bestVal === Infinity || bestVal === -Infinity) return;
    if (mode !== 'min' && bestVal <= 0) return;
    winners.set(col, new Set(bestRows));
  });
  return function didParseCell(data) {
    if (data.section !== 'body') return;
    const set = winners.get(data.column.index);
    if (!set) return;
    if (!set.has(data.row.index)) return;
    data.cell.styles.fontStyle = 'bold';
    data.cell.styles.textColor = COLORS.green;
    data.cell.styles.fillColor = COLORS.greenSoft;
  };
}

// ─── archetype table ──────────────────────────────────────────────────────
function drawArchetypeTable(pdf, x, y, w, byArchetype) {
  const rows = Array.isArray(byArchetype) ? byArchetype : (byArchetype?.archetypes || byArchetype?.rows || []);
  const list = (rows || [])
    .map((r) => ({
      archetype_name: String(r.archetype_name ?? r.name ?? '—').trim() || '—',
      persona_count: Number(r.persona_count ?? r.personas ?? 0) || 0,
      avg_touches_per_persona: Number(
        r.avg_touches_per_persona ?? r.avg_touches ?? r.touches_per_persona,
      ),
      total_sent: Number(r.total_sent ?? r.sent ?? 0) || 0,
      open_rate: Number(r.open_rate),
      click_rate: Number(r.click_rate),
      reply_rate: Number(r.reply_rate),
    }))
    // Drop the catch-all "(unmapped)" bucket — personas with no archetype
    // assigned aren't an archetype and clutter the exec-facing breakdown.
    .filter((r) => {
      const n = r.archetype_name.toLowerCase().replace(/[()]/g, '').trim();
      return n && n !== 'unmapped' && n !== 'unassigned' && r.archetype_name !== '—';
    })
    .sort((a, b) => b.total_sent - a.total_sent);

  const body = list.length
    ? list.map((r) => [
        truncate(r.archetype_name, 42),
        fmtInt(r.persona_count),
        Number.isFinite(r.avg_touches_per_persona) ? fmtNum(r.avg_touches_per_persona, 1) : '—',
        fmtInt(r.total_sent),
        fmtPct(r.open_rate, 1),
        fmtPct(r.click_rate, 1),
        fmtPct(r.reply_rate, 1),
      ])
    : [['—', '—', '—', '—', '—', '—', '—']];

  const didParseCell = makeBestHighlighter(body, [
    { col: 4, mode: 'max' },
    { col: 5, mode: 'max' },
    { col: 6, mode: 'max' },
  ]);

  autoTable(pdf, {
    startY: y,
    margin: { left: M, right: M },
    tableWidth: w,
    head: [['Archetype', 'Personas', 'Avg Touches', 'Sent', 'Open %', 'Click %', 'Reply %']],
    body,
    theme: 'striped',
    headStyles: { fillColor: COLORS.brand, textColor: COLORS.white, fontSize: 9, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 9, cellPadding: 4, halign: 'center', valign: 'middle', overflow: 'ellipsize' },
    alternateRowStyles: { fillColor: COLORS.tileBg },
    columnStyles: {
      0: { halign: 'left', cellWidth: 280 },
      1: { halign: 'center', cellWidth: 70 },
      2: { halign: 'center', cellWidth: 90 },
      3: { halign: 'center', cellWidth: 70 },
      4: { halign: 'center', cellWidth: 85 },
      5: { halign: 'center', cellWidth: 85 },
      6: { halign: 'center', cellWidth: w - 280 - 70 - 90 - 70 - 85 - 85 },
    },
    didParseCell,
  });
  return pdf.lastAutoTable?.finalY ?? y;
}

// ─── playbook performance table ───────────────────────────────────────────
function prettifyPlaybookKey(k) {
  const raw = String(k || '').trim();
  if (!raw) return '—';
  return raw
    .replace(/_v\d+$/i, '')
    .split('_')
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}
function drawPlaybookTable(pdf, x, y, w, byPlaybook) {
  const rows = Array.isArray(byPlaybook) ? byPlaybook : (byPlaybook?.playbooks || byPlaybook?.rows || []);
  const list = (rows || [])
    .map((r) => ({
      playbook_name:
        String(r.playbook_name ?? r.name ?? '').trim() ||
        prettifyPlaybookKey(r.playbook_key ?? r.key),
      total_sent: Number(r.total_sent ?? r.sent ?? 0) || 0,
      open_rate: Number(r.open_rate),
      click_rate: Number(r.click_rate),
      reply_rate: Number(r.reply_rate),
    }))
    .sort((a, b) => b.total_sent - a.total_sent);

  const body = list.length
    ? list.map((r) => [
        truncate(r.playbook_name, 42),
        fmtInt(r.total_sent),
        fmtPct(r.open_rate, 1),
        fmtPct(r.click_rate, 1),
        fmtPct(r.reply_rate, 1),
      ])
    : [['—', '—', '—', '—', '—']];

  const didParseCell = makeBestHighlighter(body, [
    { col: 2, mode: 'max' },
    { col: 3, mode: 'max' },
    { col: 4, mode: 'max' },
  ]);

  const sentW = 50;
  const rateW = 58;
  const nameW = Math.max(180, w - sentW - rateW * 3);

  text(pdf, 'BY PLAYBOOK', x, y - 4, { size: 9, color: COLORS.muted, weight: 'bold' });
  autoTable(pdf, {
    startY: y,
    margin: { left: x, right: PAGE_W - x - w },
    tableWidth: w,
    head: [['Playbook', 'Sent', 'Open %', 'Click %', 'Reply %']],
    body,
    theme: 'striped',
    headStyles: { fillColor: COLORS.brand, textColor: COLORS.white, fontSize: 8.5, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 8.5, cellPadding: 4, halign: 'center', valign: 'middle', overflow: 'ellipsize' },
    alternateRowStyles: { fillColor: COLORS.tileBg },
    columnStyles: {
      0: { halign: 'left', cellWidth: nameW },
      1: { halign: 'center', cellWidth: sentW },
      2: { halign: 'center', cellWidth: rateW },
      3: { halign: 'center', cellWidth: rateW },
      4: { halign: 'center', cellWidth: rateW },
    },
    didParseCell,
  });
  return pdf.lastAutoTable?.finalY ?? y;
}

// ─── ad-hoc summary ───────────────────────────────────────────────────────
function drawAdHocTable(pdf, x, y, w, adhoc) {
  const row = Array.isArray(adhoc) ? (adhoc[0] || {}) : (adhoc || {});
  const total_sent = pickNum(row, ['total_sent', 'sent']);
  const open_rate = pickNum(row, ['open_rate']);
  const click_rate = pickNum(row, ['click_rate']);
  const reply_rate = pickNum(row, ['reply_rate']);

  text(pdf, 'AD-HOC SENDS', x, y - 4, { size: 9, color: COLORS.muted, weight: 'bold' });
  autoTable(pdf, {
    startY: y,
    margin: { left: x, right: PAGE_W - x - w },
    tableWidth: w,
    head: [['Metric', 'Value']],
    body: [
      ['Total ad-hoc sends', fmtInt(total_sent)],
      ['Open rate', fmtPct(open_rate, 1)],
      ['Click rate', fmtPct(click_rate, 1)],
      ['Reply rate', fmtPct(reply_rate, 1)],
    ],
    theme: 'striped',
    headStyles: { fillColor: COLORS.brand, textColor: COLORS.white, fontSize: 8.5, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 9, cellPadding: 6, halign: 'left', valign: 'middle' },
    alternateRowStyles: { fillColor: COLORS.tileBg },
    columnStyles: {
      0: { halign: 'left', cellWidth: w * 0.6 },
      1: { halign: 'right', cellWidth: w * 0.4, fontStyle: 'bold' },
    },
  });
  return pdf.lastAutoTable?.finalY ?? y;
}

function labelAdHocCtaFamilyForPdf(key) {
  const k = String(key || '').trim();
  const map = {
    soft_binary: 'Soft-binary (yes/no question)',
    action_oriented: 'Action-oriented (meeting ask)',
    other: 'Other',
    no_cta: 'No CTA',
  };
  if (map[k]) return map[k];
  return prettifyPlaybookKey(k);
}

/** Full-width breakdown; preserves BE row order (no client sort). */
function drawAdHocByCtaFamilyTable(pdf, x, y, w, adhoc) {
  const row = Array.isArray(adhoc) ? (adhoc[0] || {}) : (adhoc || {});
  const rows = Array.isArray(row.by_cta_family) ? row.by_cta_family : [];
  if (rows.length === 0) return y;

  const list = rows.map((r) => ({
    label: labelAdHocCtaFamilyForPdf(r.cta_family ?? r.key),
    total_sent: Number(r.total_sent ?? r.sent ?? 0) || 0,
    open_rate: Number(r.open_rate),
    click_rate: Number(r.click_rate),
    reply_rate: Number(r.reply_rate),
  }));

  const body = list.map((r) => [
    truncate(r.label, 48),
    fmtInt(r.total_sent),
    fmtPct(r.open_rate, 1),
    fmtPct(r.click_rate, 1),
    fmtPct(r.reply_rate, 1),
  ]);

  const didParseCell = makeBestHighlighter(body, [
    { col: 2, mode: 'max' },
    { col: 3, mode: 'max' },
    { col: 4, mode: 'max' },
  ]);

  const sentW = 50;
  const rateW = 58;
  const nameW = Math.max(200, w - sentW - rateW * 3);

  text(pdf, 'AD-HOC PERFORMANCE BY CTA FAMILY', x, y - 4, { size: 9, color: COLORS.muted, weight: 'bold' });
  autoTable(pdf, {
    startY: y,
    margin: { left: x, right: PAGE_W - x - w },
    tableWidth: w,
    head: [['CTA Family', 'Sent', 'Open %', 'Click %', 'Reply %']],
    body,
    theme: 'striped',
    headStyles: { fillColor: COLORS.brand, textColor: COLORS.white, fontSize: 8.5, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 8.5, cellPadding: 4, halign: 'center', valign: 'middle', overflow: 'ellipsize' },
    alternateRowStyles: { fillColor: COLORS.tileBg },
    columnStyles: {
      0: { halign: 'left', cellWidth: nameW },
      1: { halign: 'center', cellWidth: sentW },
      2: { halign: 'center', cellWidth: rateW },
      3: { halign: 'center', cellWidth: rateW },
      4: { halign: 'center', cellWidth: rateW },
    },
    didParseCell,
  });
  return pdf.lastAutoTable?.finalY ?? y;
}

// ─── most active accounts mini table ──────────────────────────────────────
// Mirrors the on-screen Most Active Accounts table: the headline metric is
// Opens per Persona (total human opens ÷ personas sent — scanner-filtered
// "true" opens), sorted descending. Raw open/reply COUNTS were misleading
// here (ordering was by send volume, and Reply was near-always 0).
function drawMostActiveTable(pdf, x, y, w, topAccounts) {
  const rows = Array.isArray(topAccounts) ? topAccounts : (topAccounts?.accounts || topAccounts?.rows || []);
  const list = (rows || [])
    .map((r) => {
      const personas = Number(r.personas_sent ?? r.sent_personas ?? 0) || 0;
      const humanOpens = Number(r.total_human_opens ?? 0) || 0;
      const ratio = Number(
        r.opens_to_personas_ratio ?? (personas > 0 ? humanOpens / personas : 0),
      ) || 0;
      return {
        account_name: String(r.account_name ?? r.name ?? '—').trim() || '—',
        personas_sent: personas,
        human_openers: Number(r.human_openers ?? 0) || 0,
        opens_per_persona: ratio,
      };
    })
    .filter((r) => r.personas_sent > 0)
    .sort((a, b) => b.opens_per_persona - a.opens_per_persona)
    .slice(0, 10);

  const body = list.length
    ? list.map((r) => [
        truncate(r.account_name, 34),
        fmtInt(r.personas_sent),
        fmtInt(r.human_openers),
        fmtNum(r.opens_per_persona, 2),
      ])
    : [['—', '—', '—', '—']];

  // Highlight the account with the strongest opens-per-persona intensity.
  const didParseCell = makeBestHighlighter(body, [{ col: 3, mode: 'max' }]);

  const metricW = 46;
  const nameW = Math.max(150, w - metricW * 3);

  text(pdf, 'MOST ACTIVE ACCOUNTS', x, y - 4, { size: 9, color: COLORS.muted, weight: 'bold' });
  autoTable(pdf, {
    startY: y,
    margin: { left: x, right: PAGE_W - x - w },
    tableWidth: w,
    head: [['Account', 'Personas', 'Openers', 'Opens/Pers.']],
    body,
    theme: 'striped',
    headStyles: { fillColor: COLORS.brand, textColor: COLORS.white, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 8.5, cellPadding: 3, halign: 'center', valign: 'middle', overflow: 'ellipsize' },
    alternateRowStyles: { fillColor: COLORS.tileBg },
    columnStyles: {
      0: { halign: 'left', cellWidth: nameW },
      1: { halign: 'center', cellWidth: metricW },
      2: { halign: 'center', cellWidth: metricW },
      3: { halign: 'center', cellWidth: metricW },
    },
    didParseCell,
  });
  return pdf.lastAutoTable?.finalY ?? y;
}

// ─── disengaged accounts block ────────────────────────────────────────────
const REASON_LABELS = {
  no_fit: 'Not a fit',
  no_budget: 'No budget',
  not_interested: 'Not interested',
  wrong_timing: 'Wrong timing',
  competitor_chosen: 'Competitor chosen',
  no_response: 'No response',
  contacted_externally: 'Contacted externally',
  duplicate: 'Duplicate',
  other: 'Other',
};

function buildDisengagedSummary(disengagedResp) {
  const rows = Array.isArray(disengagedResp)
    ? disengagedResp
    : (disengagedResp?.accounts || disengagedResp?.rows || []);
  const counts = new Map();
  let total = 0;
  for (const r of rows || []) {
    const reasonRaw = String(r?.is_visible_reason ?? r?.reason ?? '').toLowerCase().trim();
    if (!reasonRaw || reasonRaw === 'not_added') continue;
    total += 1;
    counts.set(reasonRaw, (counts.get(reasonRaw) || 0) + 1);
  }
  const sorted = Array.from(counts.entries())
    .map(([key, n]) => ({ key, label: REASON_LABELS[key] || key.replace(/_/g, ' '), value: n }))
    .sort((a, b) => b.value - a.value);
  return { total, reasons: sorted };
}

function drawDisengagedBlock(pdf, x, y, w, h, disengagedResp) {
  fillRect(pdf, x, y, w, h, COLORS.white);
  strokeRect(pdf, x, y, w, h, COLORS.border, 0.5);
  text(pdf, 'DISENGAGED ACCOUNTS', x + 8, y + 12, { size: 8, color: COLORS.muted, weight: 'bold' });
  const { total, reasons } = buildDisengagedSummary(disengagedResp);
  text(pdf, fmtInt(total), x + 12, y + 40, { size: 24, color: COLORS.text, weight: 'bold' });
  text(pdf, 'accounts hidden', x + 12, y + 52, { size: 8, color: COLORS.muted });

  const listX = x + 110;
  const listW = w - 110 - 8;
  if (reasons.length === 0) {
    text(pdf, 'No disengaged accounts.', listX, y + 30, { size: 9, color: COLORS.muted });
    return;
  }
  const top = reasons.slice(0, 6);
  const topVal = top[0].value;
  const lineH = Math.max(11, Math.min(22, (h - 28) / Math.max(top.length, 1)));
  top.forEach((r, i) => {
    const ly = y + 26 + i * lineH;
    text(pdf, truncate(r.label, 22), listX, ly + 6, { size: 8, color: COLORS.text });
    const barX = listX + 110;
    const barMaxW = listW - 110 - 36;
    pdf.setFillColor(240, 240, 240);
    pdf.rect(barX, ly + 2, barMaxW, 5, 'F');
    fillRect(pdf, barX, ly + 2, (r.value / topVal) * barMaxW, 5, COLORS.amber);
    text(pdf, fmtInt(r.value), listX + listW - 4, ly + 6, { size: 8, color: COLORS.text, align: 'right' });
  });
}

// ─── geographic activity table ────────────────────────────────────────────
function drawGeoActivityTable(pdf, x, y, w, h, country) {
  text(pdf, 'GEOGRAPHIC ACTIVITY', x, y - 4, { size: 9, color: COLORS.muted, weight: 'bold' });

  const cells = (country?.cells || [])
    .filter(
      (c) =>
        c.country &&
        String(c.country).toLowerCase() !== '(unknown)' &&
        (Number(c.total_sends ?? c.sends ?? 0) > 0 || Number(c.account_count ?? 0) > 0),
    )
    .map((c) => ({
      country: c.country,
      sends: Number(c.total_sends ?? c.sends) || 0,
      open_rate: Number(c.avg_open_rate ?? c.open_rate),
      click_rate: Number(c.avg_click_rate ?? c.click_rate),
      reply_rate: Number(c.avg_reply_rate ?? c.reply_rate),
    }))
    .sort((a, b) => b.sends - a.sends);

  const maxRows = Math.max(4, Math.floor((h - 28) / 18));
  const top = cells.slice(0, Math.min(maxRows, 24));

  const body = top.length
    ? top.map((c) => [
        truncate(c.country, 22),
        fmtInt(c.sends),
        fmtPct(c.open_rate, 0),
        Number.isFinite(c.click_rate) ? fmtPct(c.click_rate, 0) : '—',
        Number.isFinite(c.reply_rate) ? fmtPct(c.reply_rate, 0) : '—',
      ])
    : [['—', '—', '—', '—', '—']];

  const didParseCell = makeBestHighlighter(body, [
    { col: 2, mode: 'max' },
    { col: 3, mode: 'max' },
    { col: 4, mode: 'max' },
  ]);

  autoTable(pdf, {
    startY: y,
    margin: { left: x, right: PAGE_W - x - w },
    tableWidth: w,
    head: [['Country', 'Sends', 'Open %', 'Click %', 'Reply %']],
    body,
    theme: 'striped',
    headStyles: { fillColor: COLORS.brand, textColor: COLORS.white, fontSize: 8.5, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 8.5, cellPadding: 4, halign: 'center', valign: 'middle', overflow: 'ellipsize' },
    alternateRowStyles: { fillColor: COLORS.tileBg },
    columnStyles: {
      0: { halign: 'left', cellWidth: Math.max(120, w - 60 - 60 - 60 - 60) },
      1: { halign: 'center', cellWidth: 60 },
      2: { halign: 'center', cellWidth: 60 },
      3: { halign: 'center', cellWidth: 60 },
      4: { halign: 'center', cellWidth: 60 },
    },
    didParseCell,
  });
  return pdf.lastAutoTable?.finalY ?? y;
}

// ─── word cloud (page 3) ──────────────────────────────────────────────────
// Port of /components/analytics/MessageKeywordsWordCloud.jsx — same spiral
// layout + reply_rate→color mapping, but rendered with jsPDF primitives so
// the PDF stays vector and the words are sharp at any zoom level.
const CLOUD_MIN_FONT = 7;
const CLOUD_MAX_FONT = 16;
// PDF export: horizontal only — jsPDF rotated-text anchors do not match SVG AABB.
const CLOUD_ROTATIONS = [0];
const CLOUD_LAYOUT_INSET = 12;

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Match FE colorForReplyRate but return [r,g,b] for jsPDF setTextColor. */
function colorForReplyRatePdf(replyRate) {
  const t = Math.max(0, Math.min(1, Number(replyRate) / 100));
  if (t <= 0) return [148, 163, 184]; // slate-400
  if (t >= 0.85) return [52, 211, 153]; // emerald-400
  const r0 = 148, g0 = 163, b0 = 184;
  const r1 = 52,  g1 = 211, b1 = 153;
  return [
    Math.round(r0 + (r1 - r0) * t),
    Math.round(g0 + (g1 - g0) * t),
    Math.round(b0 + (b1 - b0) * t),
  ];
}

/** Conservative box for center-anchored helvetica bold (align center, baseline middle). */
function estimateBox(pdf, textStr, fontSize, rotateDeg) {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(fontSize);
  const tw = pdf.getTextWidth(textStr) * 1.22;
  const th = fontSize * 1.55;
  if (rotateDeg === 0) return { w: tw, h: th };
  const rad = (rotateDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const inflate = 1.35;
  return {
    w: (tw * cos + th * sin) * inflate,
    h: (tw * sin + th * cos) * inflate,
  };
}

function beginClipRect(pdf, x, y, w, h) {
  pdf.saveGraphicsState();
  pdf.rect(x, y, w, h);
  pdf.clip();
}

function endClipRect(pdf) {
  pdf.restoreGraphicsState();
}

function layoutWords(pdf, items, width, height, minFont, maxFont) {
  if (!width || !height || !items?.length) return [];
  const weights = items.map((w) => Number(w.weight) || 0);
  const maxW = Math.max(...weights, 1);
  const minW = Math.min(...weights, 0);
  const sorted = [...items].sort((a, b) => (Number(b.weight) || 0) - (Number(a.weight) || 0));
  const placed = [];
  const boxes = [];
  const cx = width / 2;
  const cy = height / 2;
  const PAD = CLOUD_LAYOUT_INSET;
  const gap = 3;

  for (const word of sorted) {
    const wgt = Number(word.weight) || 0;
    const t = maxW === minW ? 1 : (wgt - minW) / (maxW - minW);
    const fontSize = minFont + t * (maxFont - minFont);
    const rotate = CLOUD_ROTATIONS[hashString(word.text) % CLOUD_ROTATIONS.length];
    const { w, h } = estimateBox(pdf, word.text, fontSize, rotate);

    if (w + PAD * 2 > width || h + PAD * 2 > height) continue;

    let placedOk = false;
    for (let i = 0; i < 2000 && !placedOk; i += 1) {
      const angle = i * 0.42;
      const radius = 6 + i * 1.05;
      const x = cx + Math.cos(angle) * radius - w / 2;
      const y = cy + Math.sin(angle) * radius - h / 2;
      const inBounds = x >= PAD && y >= PAD && x + w <= width - PAD && y + h <= height - PAD;
      const collides = boxes.some(
        (b) =>
          !(
            x + w + gap < b.x ||
            b.x + b.w + gap < x ||
            y + h + gap < b.y ||
            b.y + b.h + gap < y
          ),
      );
      if (inBounds && !collides) {
        boxes.push({ x, y, w, h });
        placed.push({
          text: word.text,
          reply_rate: word.reply_rate ?? 0,
          x: x + w / 2,
          y: y + h / 2,
          fontSize,
          rotate,
        });
        placedOk = true;
      }
    }
  }
  return placed;
}

export function normalizeKeywordList(raw) {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((item) => {
      if (typeof item === 'string') {
        const t = item.trim();
        return t ? { text: t, weight: 1, messages: 0, open_rate: 0, reply_rate: 0 } : null;
      }
      const t = String(item.text ?? item.word ?? item.keyword ?? item.term ?? '').trim();
      if (!t) return null;
      return {
        text: t,
        weight:
          Number(item.weight ?? item.count ?? item.messages ?? item.total ?? item.frequency) || 1,
        messages:
          Number(item.messages ?? item.message_count ?? item.total_sent ?? item.count) || 0,
        open_rate: Number(item.open_rate) || 0,
        reply_rate: Number(item.reply_rate) || 0,
      };
    })
    .filter(Boolean);
}

export function parseKeywordsPayload(data) {
  if (!data || typeof data !== 'object') return { subject: [], body: [] };
  return {
    subject: normalizeKeywordList(
      data.subject_keywords ??
        data.subject_lines ??
        data.by_subject ??
        data.subject ??
        data.subjects,
    ),
    body: normalizeKeywordList(
      data.body_keywords ?? data.message_body ?? data.by_body ?? data.body ?? data.bodies,
    ),
  };
}

function drawWordCloud(pdf, x, y, w, h, title, items) {
  fillRect(pdf, x, y, w, h, COLORS.white);
  strokeRect(pdf, x, y, w, h, COLORS.border, 0.5);
  text(pdf, title.toUpperCase(), x + 8, y + 12, { size: 7.5, color: COLORS.muted, weight: 'bold' });

  // Inner cloud area — light slate background, matching the FE component.
  const innerPad = 6;
  const innerTop = 22;
  const innerX = x + innerPad;
  const innerY = y + innerTop;
  const innerW = w - innerPad * 2;
  const innerH = h - innerTop - innerPad;
  fillRect(pdf, innerX, innerY, innerW, innerH, COLORS.cloudBg, 2);
  strokeRect(pdf, innerX, innerY, innerW, innerH, COLORS.cloudBorder, 0.3, 2);

  const normalized = normalizeKeywordList(items);
  if (normalized.length === 0) {
    text(pdf, 'No keyword signal in this window', innerX + innerW / 2, innerY + innerH / 2 + 3, {
      size: 9, color: COLORS.muted, align: 'center',
    });
    return;
  }

  const layoutW = Math.max(40, innerW - CLOUD_LAYOUT_INSET * 2);
  const layoutH = Math.max(40, innerH - CLOUD_LAYOUT_INSET * 2);
  const placed = layoutWords(pdf, normalized, layoutW, layoutH, CLOUD_MIN_FONT, CLOUD_MAX_FONT);

  beginClipRect(pdf, innerX, innerY, innerW, innerH);
  pdf.setFont('helvetica', 'bold');
  const drawOffsetX = innerX + CLOUD_LAYOUT_INSET;
  const drawOffsetY = innerY + CLOUD_LAYOUT_INSET;
  for (const wpos of placed) {
    const [r, g, b] = colorForReplyRatePdf(wpos.reply_rate);
    pdf.setFontSize(wpos.fontSize);
    pdf.setTextColor(r, g, b);
    pdf.text(wpos.text, drawOffsetX + wpos.x, drawOffsetY + wpos.y, {
      angle: -wpos.rotate,
      align: 'center',
      baseline: 'middle',
    });
  }
  endClipRect(pdf);
}

// ─── PAGE 1 ────────────────────────────────────────────────────────────────
function renderPage1(pdf, {
  summary, prior, dist, daily, byArchetype, tenantLabel, days, periodLabel, scopeSuffix, logoDataUrl,
}) {
  const descBase = `Pipeline Performance Overview · ${periodLabel || `Last ${days} days`}`;
  const description = scopeSuffix ? `${descBase} · ${scopeSuffix}` : descBase;
  drawHeaderBand(pdf, {
    tenantLabel,
    description,
    pageStr: 'Page 1 of 4',
    generatedOn: new Date(),
  });

  const sent = pickNum(summary, ['total_sent', 'total_emails_sent']);
  // [2026-07-08] Accounts tile = distinct real accounts touched, from the
  // overview endpoint's `active_accounts` (now resolved through the persona
  // canon → account map, BE fix same date). Previously this tile was fed the
  // FE's persona-count estimate (sum of persona_count across sectors), which
  // labelled a ~4k persona total as "Accounts" — wildly wrong for a tenant
  // with ~370 accounts. Persona counts belong to the distribution pies, not
  // this tile.
  const acc = pickNum(summary, ['active_accounts', 'accounts_reached', 'distinct_accounts', 'unique_accounts']);
  const avgTouches = computeAvgTouches(summary);

  const kpis = [
    {
      label: 'Accounts',
      value: fmtInt(acc),
      // prior.active_accounts is a window-differential, not directly comparable
      // to the current distinct count — leave the delta blank rather than mislead.
      delta: { color: COLORS.muted, text: '—' },
    },
    { label: 'Sends', value: fmtInt(sent), delta: fmtDelta(sent, prior?.total_sent) },
    { label: 'Open %', value: fmtPct(summary?.open_rate, 1), delta: fmtRateDelta(summary?.open_rate, prior?.open_rate) },
    { label: 'Click %', value: fmtPct(summary?.click_rate, 1), delta: fmtRateDelta(summary?.click_rate, prior?.click_rate) },
    { label: 'Reply %', value: fmtPct(summary?.reply_rate, 1), delta: fmtRateDelta(summary?.reply_rate, prior?.reply_rate) },
    { label: 'Avg Touches', value: fmtNum(avgTouches, 1), delta: fmtDelta(avgTouches, prior?.avg_touches_per_persona) },
  ];
  const tileH = 70;
  const gap = 6;
  const tileW = (INNER_W - 5 * gap) / 6;
  const tilesStartX = M;
  const kpiY = 80;
  kpis.forEach((k, i) => drawTile(pdf, tilesStartX + i * (tileW + gap), kpiY, tileW, tileH, k));

  const midY = kpiY + tileH + 16;
  const midH = 200;
  drawTrendChart(pdf, M, midY, 520, midH, daily);

  const piesX = M + 520 + 8;
  const piesW = INNER_W - 520 - 8;
  const halfH = (midH - 6) / 2;
  const byCountryRows = (dist?.by_country || []).map((r) => ({
    label: r.label ?? r.name ?? r.country ?? '—',
    value: Number(r.sends ?? r.count ?? r.total_sent ?? r.persona_count ?? r.account_count) || 0,
  }));
  const bySectorRows = (dist?.by_sector || []).map((r) => ({
    label: r.label ?? r.name ?? r.sector ?? '—',
    value: Number(r.sends ?? r.count ?? r.total_sent ?? r.persona_count ?? r.account_count) || 0,
  }));
  drawPieWithLegend(pdf, piesX, midY, piesW, halfH, 'By Country', byCountryRows);
  drawPieWithLegend(pdf, piesX, midY + halfH + 6, piesW, halfH, 'By Sector', bySectorRows);

  const archY = midY + midH + 22;
  drawArchetypeTable(pdf, M, archY, INNER_W, byArchetype);

  drawBrandFooter(pdf, logoDataUrl);
}

// ─── PAGE 2 ────────────────────────────────────────────────────────────────
// [v10] AD-HOC PERFORMANCE BY CTA FAMILY moved off this page — it now lives
// on page 3 next to the word clouds.
function renderPage3MostActive(pdf, { byPlaybook, adhoc, country, topAccounts, tenantLabel, days, periodLabel, scopeSuffix, logoDataUrl }) {
  const descBase = `Most Active · ${periodLabel || `Last ${days} days`}`;
  const description = scopeSuffix ? `${descBase} · ${scopeSuffix}` : descBase;
  drawHeaderBand(pdf, { tenantLabel, description, pageStr: 'Page 3 of 4' });

  const tablesY = 96;
  const playbookW = 540;
  const adhocW = INNER_W - playbookW - 16;

  const playbookFinalY = drawPlaybookTable(pdf, M, tablesY, playbookW, byPlaybook);
  const adhocFinalY = drawAdHocTable(pdf, M + playbookW + 16, tablesY, adhocW, adhoc);

  const tablesEndY = Math.max(playbookFinalY, adhocFinalY);

  const colsTopY = tablesEndY + 22;
  const leftW = 380;
  const rightX = M + leftW + 16;
  const rightW = INNER_W - leftW - 16;

  const bottomLimit = PAGE_H - FOOTER_STRIP_H - FOOTER_STRIP_PAD_BOTTOM - 6;
  const colsAvailH = bottomLimit - colsTopY;

  // [2026-07-08] Disengaged block moved to the new page 2 (Leads Generated).
  drawMostActiveTable(pdf, M, colsTopY, leftW, topAccounts);
  drawGeoActivityTable(pdf, rightX, colsTopY, rightW, colsAvailH, country);

  drawBrandFooter(pdf, logoDataUrl);
}

// ─── PAGE 2 (NEW) — Leads Generated + Disengaged ───────────────────────────
// [2026-07-08] Dedicated page for pipeline health: qualified-lead composition
// (accounts marked is_qualified) by sector / country / target-products, plus
// the Disengaged breakdown moved here off the old page 2. Mirrors the on-screen
// "Leads Generated" section.
function renderPage2Leads(pdf, { dist, disengaged, tenantLabel, days, periodLabel, scopeSuffix, logoDataUrl }) {
  const descBase = `Leads Generated · ${periodLabel || `Last ${days} days`}`;
  const description = scopeSuffix ? `${descBase} · ${scopeSuffix}` : descBase;
  drawHeaderBand(pdf, { tenantLabel, description, pageStr: 'Page 2 of 4' });

  const ql = dist?.qualified_leads || {};
  const totalQualified = Number(ql?.total_qualified ?? 0);

  const headingY = 82;
  text(pdf, 'QUALIFIED LEADS', M, headingY, { size: 9, color: COLORS.muted, weight: 'bold' });
  text(pdf, `${fmtInt(totalQualified)} account${totalQualified === 1 ? '' : 's'} marked qualified`, M + 140, headingY, {
    size: 9, color: COLORS.text,
  });

  const toPieRows = (rows) =>
    (Array.isArray(rows) ? rows : []).map((r) => ({
      label: r.label ?? r.name ?? '—',
      value: Number(r.account_count ?? r.count ?? 0) || 0,
    }));

  const piesY = headingY + 10;
  const pieGap = 16;
  const pieW = (INNER_W - pieGap * 2) / 3;
  const pieH = 190;
  drawPieWithLegend(pdf, M, piesY, pieW, pieH, 'Qualified · By Sector', toPieRows(ql.by_sector));
  drawPieWithLegend(pdf, M + pieW + pieGap, piesY, pieW, pieH, 'Qualified · By Country', toPieRows(ql.by_country));
  drawPieWithLegend(pdf, M + (pieW + pieGap) * 2, piesY, pieW, pieH, 'Qualified · By Target Products', toPieRows(ql.by_target_products));

  const bottomLimit = PAGE_H - FOOTER_STRIP_H - FOOTER_STRIP_PAD_BOTTOM - 6;
  const disY = piesY + pieH + 24;
  const disH = bottomLimit - disY;
  drawDisengagedBlock(pdf, M, disY, INNER_W, disH, disengaged);

  drawBrandFooter(pdf, logoDataUrl);
}

// ─── PAGE 3 ────────────────────────────────────────────────────────────────
// [v11 2026-06-01] New page: AD-HOC PERFORMANCE BY CTA FAMILY moved here
// from page 2, plus two word clouds (subject lines / message body) showing
// which words correlate with engagement. Word clouds mirror the on-screen
// MessageKeywordsWordCloud component — same spiral layout, same reply-rate
// color ramp — but rendered with jsPDF primitives so the PDF stays vector
// and the words remain sharp at any zoom level.
function renderPage4Message(pdf, { adhoc, keywords, tenantLabel, days, periodLabel, scopeSuffix, logoDataUrl }) {
  const descBase = `Message Performance · ${periodLabel || `Last ${days} days`}`;
  const description = scopeSuffix ? `${descBase} · ${scopeSuffix}` : descBase;
  drawHeaderBand(pdf, { tenantLabel, description, pageStr: 'Page 4 of 4' });

  const tablesY = 96;
  const ctaFinalY = drawAdHocByCtaFamilyTable(pdf, M, tablesY, INNER_W, adhoc);

  // If the BE returned no CTA-family rows the table is skipped — start the
  // clouds where the table would have begun.
  const cloudsY = (Number.isFinite(ctaFinalY) ? ctaFinalY : tablesY) + 22;
  const bottomLimit = PAGE_H - FOOTER_STRIP_H - FOOTER_STRIP_PAD_BOTTOM - 6;
  const cloudsH = Math.max(160, bottomLimit - cloudsY);
  const gap = 16;
  const cloudW = (INNER_W - gap) / 2;

  const parsed = parseKeywordsPayload(keywords);
  drawWordCloud(pdf, M, cloudsY, cloudW, cloudsH,
    'Words Driving Engagement — Subject Lines', parsed.subject);
  drawWordCloud(pdf, M + cloudW + gap, cloudsY, cloudW, cloudsH,
    'Words Driving Engagement — Message Body', parsed.body);

  drawBrandFooter(pdf, logoDataUrl);
}

/**
 * Four-page Analytics PDF.
 *
 * Page 1 — Pipeline overview: KPI tiles, trend chart, country/sector pies,
 *          archetype table (the "(unmapped)" bucket is excluded).
 * Page 2 — Leads Generated: qualified-lead composition (by sector / country /
 *          target products) + disengaged-account breakdown.
 * Page 3 — Most Active: by-playbook, ad-hoc summary, most-active accounts
 *          (ranked by opens-per-persona), geographic activity.
 * Page 4 — Message Performance: ad-hoc CTA-family breakdown + subject-line
 *          and message-body word clouds (color = reply rate, size = volume).
 *
 * [2026-07-08]
 *   • Accounts KPI now reads active_accounts (real distinct accounts), not a
 *     persona-count estimate.
 *   • Most Active Accounts ranks by opens-per-persona (scanner-filtered).
 *   • New page 2 (Leads Generated + Disengaged); pages renumbered to /4.
 *
 * [v9 — 2026-05-12]
 *   • Page 2 gap reduced 60 → 22; slim navy footer band on every page.
 *   • Page 1 header carries "Generated on:" stamp.
 *   • Disengaged reasons stretch to fill their bounding box.
 * [v10 — 2026-05-12] Page 2: AD-HOC PERFORMANCE BY CTA FAMILY appended
 *   below the bottom trio when `by_cta_family` was non-empty.
 * [v11 — 2026-06-01] CTA Family table moved to a new page 3, joined by two
 *   word clouds (GET /api/email-analytics/keywords?product_context=ALL).
 *   Function name kept as `downloadAnalyticsTwoPagePdf` for caller
 *   compatibility — output is now 3 pages.
 */
export async function downloadAnalyticsTwoPagePdf({
  apiBaseUrl,
  tenantId,
  tenantLabel,
  days,
  analyticsWindow,
  userId,
  scopeSuffix,
  logoDataUrl,
  timeSavedHours: _timeSavedHours,
  // [2026-07-08] `activePersonasCount` removed — it used to feed the page-1
  // "Accounts" tile with a persona total. The tile now reads the overview's
  // `active_accounts`. Accepted-and-ignored here for caller back-compat.
  activePersonasCount: _activePersonasCount,
  /** Prior-window rates from Analytics overview (ensures PDF deltas match the UI). */
  priorPeriodRates,
}) {
  const base = resolveApiBaseUrl(apiBaseUrl);
  const window = coerceAnalyticsWindow(analyticsWindow || days);
  const d = String(window.mode === 'preset' ? window.days : windowDayCount(window));
  const nDays = windowDayCount(window);
  const periodLabel = formatAnalyticsWindowLabel(window);

  const qsBase = new URLSearchParams({ tenant_id: tenantId });
  appendAnalyticsWindow(qsBase, window);
  if (userId) qsBase.append('user_id', userId);

  const qsVis = new URLSearchParams(qsBase);
  qsVis.append('only_visible_accounts', 'true');
  qsVis.append('only_visible_personas', 'true');

  const qsTop = new URLSearchParams(qsVis);
  qsTop.append('limit', '10');

  const qsDis = new URLSearchParams({ tenant_id: tenantId, limit: '200', offset: '0' });

  // Keywords: mirror the on-screen MessagePerformanceInsights default
  // (product_context=ALL) so the PDF reflects what the user sees in the
  // unfiltered view of the analytics page.
  const qsKw = new URLSearchParams(qsBase);
  qsKw.append('product_context', 'ALL');

  // Bounded-concurrency fetch (see runPool/fetchJson notes above). Firing all
  // of these with Promise.all made Postgres time out random endpoints, blanking
  // PDF sections; a pool of 3 keeps contention low and each call fast. The logo
  // (data-URI/local) is cheap and runs alongside, outside the DB pool.
  const [
    summaryRaw,
    priorRaw,
    dist,
    daily,
    byArchetype,
    byPlaybook,
    adhoc,
    country,
    disengaged,
    topAccounts,
    keywordsRaw,
  ] = await runPool([
    () => fetchEmailOverviewRow(base, tenantId, window, userId),
    () => fetchEmailOverviewRow(
      base,
      tenantId,
      window.mode === 'custom'
        ? { mode: 'custom', from: previousInclusiveRange(window.from, window.to).from, to: window.to }
        : String(nDays * 2),
      userId,
    ),
    () => fetchJson(`${base}/api/email-analytics/distribution?${qsVis.toString()}`),
    () => fetchJson(`${base}/api/email-analytics/daily-engagement?${qsBase.toString()}`),
    () => fetchJson(`${base}/api/email-analytics/by-archetype?${qsVis.toString()}`),
    () => fetchJson(`${base}/api/email-analytics/by-playbook?${qsBase.toString()}`),
    () => fetchJson(`${base}/api/email-analytics/ad-hoc?${qsBase.toString()}`),
    () => fetchJson(`${base}/api/email-analytics/country-heatmap?${qsVis.toString()}`),
    () => fetchJson(`${base}/api/accounts/disengaged?${qsDis.toString()}`),
    () => fetchJson(`${base}/api/email-analytics/top-accounts?${qsTop.toString()}`),
    () => fetchJson(`${base}/api/email-analytics/keywords?${qsKw.toString()}`),
  ], 3);
  const resolvedLogo = await loadLogoDataUrl(logoDataUrl);

  const summaryRow = enrichSummaryRow(normalizeRow(summaryRaw));
  const priorFull = enrichSummaryRow(normalizeRow(priorRaw));
  const priorOnly = applyPriorPeriodRateOverrides(
    buildPriorOnly(summaryRow, priorFull),
    priorPeriodRates,
  );
  const label = tenantLabel || formatTenantLabelForPdf(tenantId);

  try {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    renderPage1(pdf, {
      summary: summaryRow,
      prior: priorOnly,
      dist,
      daily,
      byArchetype,
      tenantLabel: label,
      days: d,
      periodLabel,
      scopeSuffix,
      logoDataUrl: resolvedLogo,
    });
    // Page 2 (new) — Leads Generated (qualified-lead composition) + Disengaged.
    pdf.addPage('a4', 'landscape');
    renderPage2Leads(pdf, {
      dist,
      disengaged,
      tenantLabel: label,
      days: d,
      periodLabel,
      scopeSuffix,
      logoDataUrl: resolvedLogo,
    });
    // Page 3 — Most Active (playbook, ad-hoc, most-active accounts, geographic).
    pdf.addPage('a4', 'landscape');
    renderPage3MostActive(pdf, {
      byPlaybook,
      adhoc,
      country,
      topAccounts,
      tenantLabel: label,
      days: d,
      periodLabel,
      scopeSuffix,
      logoDataUrl: resolvedLogo,
    });
    // Page 4 — Message Performance (CTA family + word clouds).
    pdf.addPage('a4', 'landscape');
    renderPage4Message(pdf, {
      adhoc,
      keywords: keywordsRaw,
      tenantLabel: label,
      days: d,
      periodLabel,
      scopeSuffix,
      logoDataUrl: resolvedLogo,
    });
    const day = new Date().toISOString().slice(0, 10);
    pdf.save(`neogtm-analytics-${tenantId}-${day}.pdf`);
  } catch (err) {
    console.error('[analyticsPdfExport] PDF generation failed', err);
    throw err;
  }
}
